import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-gratitude",
  title: "A prayer of gratitude — on an ordinary day",
  h1: "A prayer of gratitude",
  description:
    "A thanksgiving prayer for a day that was fine rather than remarkable, four passages, and why in everything does not mean for everything.",
  targets: [
    "prayer of gratitude",
    "thanksgiving prayer",
    "prayer to thank god",
    "prayer for a grateful heart",
  ],
  published: "2026-09-03",
  standfirst:
    "Gratitude is easy after good news and almost impossible on a Wednesday, which is when it does the most work.",
  takeaways: [
    'Be specific. "Thank you for everything" is the gratitude equivalent of "sorry for everything" — it costs nothing and names nothing.',
    "1 Thessalonians 5:18 says give thanks in everything, not for everything. The preposition is the whole difference and it is routinely misquoted.",
    "This is not a technique for feeling better. It sometimes works that way; when it is done in order to work that way it usually stops working.",
  ],
  when: [
    "At the end of a day where nothing went wrong and nothing went right.",
    "Before a meal, out loud, when everyone would rather you did not.",
    "After you have spent an hour comparing your life with somebody's edited version of theirs.",
    "On the drive home from something that went better than you deserved.",
  ],
  prayer: [
    "God, today was fine. Nothing happened. I am aware that a great many people would take this day from me without hesitating.",
    "Thank you for these three specific things, which I am naming rather than gesturing at, because I have noticed the general version does not touch me at all.",
    "Thank you for the ones I did not notice — the thing that did not go wrong, the illness nobody got, the conversation that could have gone badly and did not.",
    "I am not going to pretend the hard part is a gift. It is not, and calling it one has never helped me. But I will say thank you in the middle of it, which is what I was actually asked to do.",
    "Make me the kind of person who says this on the days it is not obvious.",
  ],
  short:
    "God, thank you for these three specific things. I would have missed them.",
  honest: [
    "This prayer does not require you to be grateful for what is hurting you. In everything is not for everything, and a page that blurs those two is asking somebody in the middle of a bad year to lie.",
    "It is also not a mood-management strategy dressed up. Gratitude practised in order to produce a feeling is aimed at the feeling, and the arrangement tends to collapse the first week it fails to deliver.",
    "And it does not cancel a legitimate complaint. The psalms manage thanksgiving and protest in the same book, frequently in the same psalm, without treating either as a failure of the other.",
  ],
  verses: [
    {
      verse: {
        text: "Always rejoice. Pray without ceasing. In everything give thanks, for this is the will of God in Christ Jesus toward you.",
        ref: "1 Thessalonians 5:16–18",
      },
      note: "The most misquoted preposition in the New Testament. In everything, not for everything — the instruction is about where you are standing when you give thanks, not about redescribing the situation as good. Paul is writing to people under pressure, and asking them to be grateful for the pressure would be a different and much crueller sentence.",
    },
    {
      verse: {
        text: "Praise Yahweh, my soul, and don't forget all his benefits,",
        ref: "Psalm 103:2",
      },
      note: "The psalm assumes forgetting is the default, which is the useful part. It then lists the benefits explicitly over the next three verses rather than leaving them general, and that list is the model: gratitude in the psalms is nearly always itemised, because an unitemised one slides off.",
    },
    {
      verse: {
        text: "And let the peace of God rule in your hearts, to which also you were called in one body, and be thankful.",
        ref: "Colossians 3:15",
      },
      note: "Rule is an umpiring word — it decides between competing claims. The thankfulness is attached to that rather than to circumstances, and it is plural and communal: called in one body. Gratitude here is closer to a shared practice than to a private mood.",
    },
    {
      verse: {
        text: "This is the day that Yahweh has made. We will rejoice and be glad in it!",
        ref: "Psalm 118:24",
      },
      note: "Printed on a great many kitchen walls, and worth reading in context: the psalm is a victory song sung after something went badly wrong first, and the verses around it describe being surrounded, pushed and nearly finished. This day is a specific day that followed a bad stretch, not a general observation about mornings.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Three things, named",
      id: "three-things-named",
    },
    {
      type: "p",
      text: "The version of this that works is embarrassingly small: name three specific things from the actual day. Not blessings in general, not health and family as categories — the particular cup of tea, the particular message, the particular thing that did not go wrong.",
    },
    {
      type: "p",
      text: "Specificity is what makes it real rather than performed, and it is the same rule that governs {confession} at the other end of the same practice.",
      links: {
        confession: "/prayers/prayer-for-forgiveness",
      },
    },
    {
      type: "h2",
      text: "In everything, not for everything",
      id: "in-everything-not-for-everything",
    },
    {
      type: "p",
      text: "The misquotation is not pedantry. Told to be thankful for a bereavement or an illness, a reasonable person concludes that Christianity requires them to lie about their own life, and they are right to reject that.",
    },
    {
      type: "p",
      text: "What the text asks is harder and more possible: to keep saying thank you while inside the thing, about whatever is still true. The psalms do exactly this, often in the same breath as a complaint.",
    },
    {
      type: "h2",
      text: "It is the opposite of the feed",
      id: "it-is-the-opposite-of-the-feed",
    },
    {
      type: "p",
      text: "An evening spent scrolling is an evening spent being shown what you do not have, at a rate somebody else sets. Gratitude is the same attention pointed at what is already in the room, and the two do not really coexist.",
    },
    {
      type: "note",
      text: "Which is why this prayer is most useful immediately after putting the phone down, and nearly useless while still holding it.",
    },
  ],
  faq: [
    {
      q: "What is a simple prayer of thanks?",
      a: "Name three specific things from the actual day and thank God for those. The general version — thank you for everything — names nothing and touches nothing.",
    },
    {
      q: "Does the Bible say to be thankful for everything?",
      a: "No. 1 Thessalonians 5:18 says give thanks in everything, which is about where you are standing rather than about redescribing what is happening to you as good.",
    },
    {
      q: "Does gratitude actually change anything?",
      a: "It changes what you are looking at, which over time changes a fair amount. Done in order to produce a feeling it tends to stop working, which is why the texts frame it as obedience rather than as technique.",
    },
  ],
  relatedPrayers: ["prayer-for-comparison", "morning-prayer"],
  related: ["social-media-fast-guide", "digital-sabbath"],
  relatedTopics: ["bible-verses-about-comparison"],
  relatedPassages: ["philippians-4-6-7"],
};
